import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Calculator, Lock, Play, CircleAlert, ChevronRight } from "lucide-react";
import { Pagina, Cartao, Carregando, Falha } from "../shell/Pagina.js";
import { DataTable, EmptyState, type Coluna } from "@pessoas/ui";
import { trpc } from "../lib/trpc.js";
import { moeda, competencia as fComp, plural } from "../lib/formato.js";
import { usePode } from "../shell/Sessao.js";
import { Holerite } from "./Holerite.js";
import { StepUp, pedeStepUp } from "../shell/StepUp.js";

type Linha = Awaited<ReturnType<typeof trpc.fechamento.folha.query>>[number];

const ESTADO: Record<string, string> = {
  aberta: "Aberta", calculando: "Calculando", calculada: "Calculada",
  conferida: "Conferida", fechada: "Fechada", reaberta: "Reaberta",
};

export function Fechamento() {
  const [competenciaId, setCompetenciaId] = useState<string | null>(null);
  const [abrindo, setAbrindo] = useState(false);
  const [vendo, setVendo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<null | "fechar">(null);
  const podeCalcular = usePode("folha.calcular");
  const podeFechar = usePode("folha.fechar");
  const qc = useQueryClient();

  const comps = useQuery({
    queryKey: ["folha", "competencias"],
    queryFn: () => trpc.fechamento.competencias.query(),
  });
  const atual = comps.data?.find((c) => c.id === competenciaId) ?? comps.data?.[0];

  const folha = useQuery({
    queryKey: ["folha", "linhas", atual?.id],
    queryFn: () => trpc.fechamento.folha.query({ competenciaId: atual!.id }),
    enabled: !!atual,
  });

  const calcular = useMutation({
    mutationFn: () => trpc.fechamento.calcular.mutate({
      competenciaId: atual!.id, competencia: atual!.competencia,
    }),
    onSuccess: (r) => {
      // Item sem rubrica cadastrada não é gravado em silêncio: o eSocial vai
      // cobrar a rubrica correspondente, e descobrir isso na transmissão é
      // tarde.
      setAviso(r.semRubrica.length
        ? `Calculadas ${r.calculadas} de ${r.deQuantas}. Sem rubrica cadastrada: ${r.semRubrica.join(", ")}.`
        : `Calculadas ${r.calculadas} de ${r.deQuantas}.`);
      void qc.invalidateQueries({ queryKey: ["folha"] });
    },
    onError: (e: unknown) => setAviso(e instanceof Error ? e.message : String(e)),
  });

  const fechar = useMutation({
    mutationFn: () => trpc.fechamento.fechar.mutate({
      competenciaId: atual!.id, competencia: atual!.competencia,
    }),
    onSuccess: () => { setConfirmando(null); void qc.invalidateQueries({ queryKey: ["folha"] }); },
    onError: (e: unknown) => {
      // Recusa por step-up não é erro: é o sistema pedindo a confirmação que
      // ele mesmo exige. Mostrar "acesso negado" aqui seria mentir.
      if (pedeStepUp(e)) { setConfirmando("fechar"); return; }
      setAviso(e instanceof Error ? e.message : String(e));
    },
  });

  const colunas: Coluna<Linha>[] = [
    { chave: "nome", titulo: "Nome", valor: (l) => l.nome, render: (l) => l.nome },
    { chave: "matricula", titulo: "Matrícula", valor: (l) => l.matricula,
      render: (l) => <span className="num">{l.matricula}</span> },
    { chave: "cargo", titulo: "Cargo", valor: (l) => l.cargo ?? "",
      render: (l) => l.cargo ?? "sem registro" },
    { chave: "proventos", titulo: "Proventos", numerico: true, total: true,
      valor: (l) => Number(l.totalProventos), render: (l) => moeda(l.totalProventos) },
    { chave: "descontos", titulo: "Descontos", numerico: true, total: true,
      valor: (l) => Number(l.totalDescontos), render: (l) => moeda(l.totalDescontos) },
    { chave: "liquido", titulo: "Líquido", numerico: true, total: true,
      valor: (l) => Number(l.liquido), render: (l) => <strong>{moeda(l.liquido)}</strong> },
  ];

  return (
    <Pagina
      secao="Folha" titulo="Fechamento"
      carimbo={atual ? `competência ${atual.competencia} · ${ESTADO[atual.estado]}` : undefined}
      descricao="Todo valor abre a memória de cálculo: a fórmula, os números que entraram e a norma que sustenta."
      acoes={
        <div className="flex items-center gap-2">
          {comps.data && comps.data.length > 0 && (
            <select className="campo h-8 w-auto text-xs" value={atual?.id ?? ""}
                    aria-label="Competência"
                    onChange={(e) => setCompetenciaId(e.target.value)}>
              {comps.data.map((c) => (
                <option key={c.id} value={c.id}>{c.competencia} · {c.tipo}</option>
              ))}
            </select>
          )}
          <button className="botao" onClick={() => setAbrindo(true)}>Abrir competência</button>
        </div>
      }
    >
      {comps.isPending && <Carregando linhas={4} />}
      {comps.isError && <Falha erro={comps.error} tentarDeNovo={() => void comps.refetch()} />}

      {comps.isSuccess && comps.data.length === 0 && (
        <EmptyState
          titulo="Nenhuma competência aberta"
          descricao="A folha é calculada por competência e por tipo. Abra a do mês para começar."
          acao={<button className="botao" data-variante="primario"
                        onClick={() => setAbrindo(true)}>Abrir a primeira</button>}
        />
      )}

      {atual && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {podeCalcular && atual.estado !== "fechada" && (
              <button className="botao" data-variante="primario"
                      disabled={calcular.isPending}
                      onClick={() => { setAviso(null); calcular.mutate(); }}>
                <Calculator aria-hidden size={15} />
                {calcular.isPending ? "Calculando" : "Calcular a folha"}
              </button>
            )}
            {podeFechar && atual.estado !== "fechada" && (folha.data?.length ?? 0) > 0 && (
              <button className="botao" disabled={fechar.isPending}
                      onClick={() => fechar.mutate()}>
                <Lock aria-hidden size={15} />
                {fechar.isPending ? "Fechando" : "Fechar a competência"}
              </button>
            )}
            {atual.estado === "fechada" && (
              <span className="pilula"><span aria-hidden className="sinal sinal-bom" /> fechada</span>
            )}
          </div>

          {aviso && (
            <p className="eop-erro" role="status">
              <CircleAlert aria-hidden size={15} /><span>{aviso}</span>
            </p>
          )}

          {folha.isPending && <Carregando linhas={5} />}
          {folha.isSuccess && folha.data.length === 0 && (
            <EmptyState
              titulo="Nada calculado nesta competência"
              descricao="O cálculo lê o salário vigente de cada vínculo e as rubricas vigentes na competência. Rubrica vigente hoje mas não na competência é rejeitada pelo eSocial."
            />
          )}
          {folha.isSuccess && folha.data.length > 0 && (
            <Cartao titulo={`Folha de ${fComp(atual.competencia)}`}
                    subtitulo={`${plural(folha.data.length, "pessoa", "pessoas")} · clique para abrir o holerite`}>
              <DataTable colunas={colunas} linhas={folha.data}
                         chaveDe={(l) => l.vinculoId} rotulo="Folha da competência"
                         onLinhaClick={(l) => setVendo(l.vinculoId)} />
            </Cartao>
          )}
        </>
      )}

      {abrindo && (
        <FormCompetencia aoFechar={() => setAbrindo(false)}
                         aoCriar={(id) => { setAbrindo(false); setCompetenciaId(id);
                           void qc.invalidateQueries({ queryKey: ["folha"] }); }} />
      )}
      {vendo && atual && (
        <Holerite competenciaId={atual.id} vinculoId={vendo} aoFechar={() => setVendo(null)} />
      )}
      {confirmando && (
        <StepUp acao="Fechar a competência"
                aoFechar={() => setConfirmando(null)}
                aoConfirmar={() => fechar.mutate()} />
      )}
    </Pagina>
  );
}

function FormCompetencia(
  { aoFechar, aoCriar }: { aoFechar: () => void; aoCriar: (id: string) => void },
) {
  const [competencia, setCompetencia] = useState(new Date().toISOString().slice(0, 7));
  const [tipo, setTipo] = useState("mensal");
  const [erro, setErro] = useState<string | null>(null);

  const empresas = useQuery({
    queryKey: ["cadastro", "empresas"],
    queryFn: () => trpc.cadastro.empresas.query(),
  });

  const abrir = useMutation({
    mutationFn: () => trpc.fechamento.abrir.mutate({
      empresaId: empresas.data![0]!.id, competencia,
      tipo: tipo as "mensal",
    }),
    onSuccess: (r) => aoCriar(r.id),
    onError: (e: unknown) => setErro(e instanceof Error ? e.message : String(e)),
  });

  return (
    <div className="eop-modal-fundo" role="dialog" aria-modal="true" aria-label="Abrir competência"
         onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="eop-modal" style={{ maxWidth: "26rem" }}>
        <header className="painel-topo">
          <h2 className="text-[0.9375rem] font-semibold">Abrir competência</h2>
        </header>
        <form className="painel-corpo space-y-3.5"
              onSubmit={(e) => { e.preventDefault(); setErro(null); abrir.mutate(); }}>
          <label className="block">
            <span className="eop-rotulo">Competência</span>
            <input className="campo mt-1.5" type="month" required value={competencia}
                   onChange={(e) => setCompetencia(e.target.value)} />
          </label>
          <label className="block">
            <span className="eop-rotulo">Tipo</span>
            <select className="campo mt-1.5" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="mensal">Folha mensal</option>
              <option value="adiantamento">Adiantamento</option>
              <option value="decimo_1">13º, 1ª parcela</option>
              <option value="decimo_2">13º, 2ª parcela</option>
              <option value="ferias">Férias</option>
              <option value="rescisao">Rescisão</option>
            </select>
          </label>
          {erro && (
            <p className="eop-erro" role="alert">
              <CircleAlert aria-hidden size={15} /><span>{erro}</span>
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="botao" data-variante="silencioso" onClick={aoFechar}>
              Cancelar
            </button>
            <button type="submit" className="botao" data-variante="primario"
                    disabled={abrir.isPending || !empresas.data?.length}>
              <Play aria-hidden size={15} />{abrir.isPending ? "Abrindo" : "Abrir"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
